/* Язык интерфейса. Исходные тексты в коде — русские; английский берётся из словарей `src/i18n/en/*.ts`
   (ключ — русская строка как есть). Язык читается один раз при загрузке страницы, переключение перезагружает
   страницу — поэтому `t()` можно звать и в константах на уровне модуля (моки, меню, подписи). */
export type Lang = "ru" | "en";

const KEY = "hub.lang";

function detect(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "ru" || saved === "en") return saved;
  } catch {
    /* приватный режим */
  }
  return (navigator.language || "").toLowerCase().startsWith("ru") ? "ru" : "en";
}

export const lang: Lang = detect();
/** локаль для дат и чисел */
export const locale = lang === "en" ? "en-GB" : "ru-RU";
export const isEn = lang === "en";

if (typeof document !== "undefined") document.documentElement.lang = lang;

export function setLang(next: Lang) {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* приватный режим */
  }
  location.reload();
}

const modules = import.meta.glob("../i18n/en/*.ts", { eager: true }) as Record<string, { default?: Record<string, string> }>;
const dict: Record<string, string> = {};
for (const m of Object.values(modules)) Object.assign(dict, m.default ?? {});

const missing = new Set<string>();
/** в dev: какие строки ещё не переведены — `window.__hubMissing()` в консоли */
if (import.meta.env.DEV && typeof window !== "undefined") (window as unknown as { __hubMissing: () => string[] }).__hubMissing = () => [...missing].sort();

function fill(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Перевод строки: t("Входящие") → "Inbox". Подстановки — {name}: t("Привет, {name}", { name }). */
export function t(ru: string, vars?: Record<string, string | number>): string {
  if (!isEn) return fill(ru, vars);
  const en = dict[ru];
  if (en === undefined) {
    if (import.meta.env.DEV) missing.add(ru);
    return fill(ru, vars);
  }
  // значение вида "day|days" (для tn) при обычном t() — форма множественного числа
  return fill(en.includes("|") ? (en.split("|")[1] ?? en) : en, vars);
}

/** Число с формой слова: tn(n, "диалог", "диалога", "диалогов") → «5 диалогов» / "5 conversations".
    В словаре ключ — форма «many» (диалогов), значение — "conversation|conversations". */
export function tn(n: number, one: string, few: string, many: string): string {
  if (isEn) {
    const en = dict[many];
    if (en) {
      const [s1, s2] = en.split("|");
      return `${n} ${Math.abs(n) === 1 ? s1 : (s2 ?? s1)}`; // без «|» в словаре — одна форма на все числа
    }
    if (import.meta.env.DEV) missing.add(many);
  }
  const a = Math.abs(n) % 100;
  const b = a % 10;
  const form = a > 10 && a < 20 ? many : b === 1 ? one : b >= 2 && b <= 4 ? few : many;
  return `${n} ${form}`;
}

/** Перевести все строки внутри объекта (для site.json и моков целиком). */
export function tDeep<T>(v: T): T {
  if (!isEn) return v;
  if (typeof v === "string") return t(v) as T;
  if (Array.isArray(v)) return v.map(tDeep) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = tDeep(x);
    return out as T;
  }
  return v;
}
